import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApprovalRequestListRow } from '../../../../core/models/approval/approval-request.model';
import { MapperApprovalRequestService } from '../../../../core/mappers/approval/mapper-approval-request.service';
import { ApiApprovalRequestService } from '../../../../core/services/api/approval/api-approval-request.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalApprovalRequestComponent } from '../../modals/approval/modal-approval-request/modal-approval-request.component';

@Component({
  selector: 'app-approval-inbox',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalApprovalRequestComponent],
  templateUrl: './approval-inbox.component.html',
  styleUrl: './approval-inbox.component.css',
})
export class ApprovalInboxComponent implements OnInit {
  private readonly api = inject(ApiApprovalRequestService);
  private readonly mapper = inject(MapperApprovalRequestService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  items = signal<ApprovalRequestListRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedRequestId = signal(0);

  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  filterStatus = signal('');

  readonly statusOptions = [
    { value: '', label: 'ทุกสถานะ' },
    { value: 'pending', label: 'รออนุมัติ' },
    { value: 'awaiting_forward', label: 'รอเลือกผู้อนุมัติถัดไป' },
    { value: 'approved', label: 'อนุมัติแล้ว' },
    { value: 'rejected', label: 'ปฏิเสธ' },
    { value: 'cancelled', label: 'ยกเลิก' },
  ];

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  ngOnInit(): void {
    this.loadTable();
  }

  statusLabel = (status: string) => this.mapper.statusLabel(status);
  recordModelLabel = (model: string) => this.mapper.recordModelLabel(model);

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openDetail(item: ApprovalRequestListRow): void {
    this.selectedRequestId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedRequestId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_index: number, item: ApprovalRequestListRow): number {
    return item.id;
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadTable(): void {
    const userId = this.resolveUserId();
    if (!userId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้');
      return;
    }

    this.isLoading.set(true);
    this.api
      .getApprovalRequestsForTable(
        userId,
        this.currentPage(),
        this.pageSize(),
        'pending',
        this.filterStatus() || null
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดรายการอนุมัติล้มเหลว');
        },
      });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
